import { AutocheckPricingTestActiveSplit } from "@redotech/redo-conversion-core-common/conversion/conversion-autocheck";
import { memo, useEffect } from "react";
import { StorefrontClient } from "../../../client/storefront";
import {
  PricingTestDisplay,
  setupPricingTestDisplay,
} from "./pricing-test-display";
import { usePricingTest } from "./use-pricing-test";

async function fetchProductHandles(
  storefrontClient: StorefrontClient,
  productIds: string[],
): Promise<Map<string, string>> {
  if (productIds.length === 0) {
    return new Map();
  }

  const query = `
    query GetProductHandles($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          handle
        }
      }
    }
  `;

  const gids = productIds.map((id) => `gid://shopify/Product/${id}`);

  try {
    const response = await fetch(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      `https://${(storefrontClient as any).host}/api/2026-07/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          "X-Shopify-Storefront-Access-Token": (storefrontClient as any).auth,
        },
        body: JSON.stringify({ query, variables: { ids: gids } }),
      },
    );

    if (!response.ok) {
      console.error("Failed to fetch product handles", response.statusText);
      return new Map();
    }

    const result = await response.json();
    const productHandleMap = new Map<string, string>();

    if (result.data?.nodes) {
      for (const [i, productId] of productIds.entries()) {
        const node = result.data.nodes[i];
        if (node?.handle && productId) {
          productHandleMap.set(productId, node.handle);
        }
      }
    }

    return productHandleMap;
  } catch (error) {
    console.error("Failed to fetch product handles", error);
    return new Map();
  }
}

interface PricingTestTrackerProps {
  autocheckPricingTest: AutocheckPricingTestActiveSplit;
  storefrontClient: StorefrontClient;
  currency: string;
}

export const PricingTestTracker = memo(function PricingTestTracker({
  autocheckPricingTest,
  storefrontClient,
  currency,
}: PricingTestTrackerProps) {
  const pricingTest = usePricingTest({
    autocheckPricingTest,
    storefrontClient,
  });

  useEffect(() => {
    if (!pricingTest.active) {
      return;
    }

    let display: PricingTestDisplay | null = null;

    const setupDisplay = async () => {
      const uniqueProductIds = new Set<string>();
      for (const treatment of autocheckPricingTest.treatments) {
        for (const variantPrice of treatment.pricingConfig.variantPrices) {
          if (variantPrice.productId) {
            uniqueProductIds.add(variantPrice.productId);
          }
        }
      }

      const productHandles = await fetchProductHandles(
        storefrontClient,
        Array.from(uniqueProductIds),
      );

      const productToFirstVariant = new Map<string, string>();
      for (const treatment of autocheckPricingTest.treatments) {
        for (const variantPrice of treatment.pricingConfig.variantPrices) {
          if (variantPrice.productId) {
            const handle = productHandles.get(variantPrice.productId);
            if (handle && !productToFirstVariant.has(handle)) {
              productToFirstVariant.set(handle, variantPrice.variantId);
            }
          }
        }
      }

      display = setupPricingTestDisplay({
        variantPrices: pricingTest.variantPrices,
        productToFirstVariant,
        currency,
      });
    };

    void setupDisplay();

    return () => {
      display?.cleanup();
    };
  }, [pricingTest, currency, autocheckPricingTest, storefrontClient]);

  return null;
});
